/**
 * Best-effort delivery-attribute seeding for the cart drawer.
 *
 * Writes `Liefertermin` (and companions) to the cart via /cart/update.js BEFORE the
 * customer navigates to checkout, so the checkout's first render already has the
 * attribute the `hide-ups` Cart Delivery Options Transform function reads. Without
 * this, the checkout UI extension writes it after mount, which visibly recomputes
 * shipping/totals once checkout has already rendered.
 *
 * This is deliberately a rough guess, NOT a full reimplementation of the checkout
 * extension's date logic:
 * - No shipping-pause awareness (would require a Storefront API call from here).
 * - No destination-Bundesland-specific holidays — uses the warehouse (Saarland)
 *   holiday calendar only, for both the shipping-day and delivery-day checks.
 * - Checks the *normal* order deadline (1 business day before the shipping date,
 *   08:00) so the guess isn't already-expired on arrival — but not the
 *   fish/poultry-specific lead time (2 business days) or earlier deadline hour
 *   (03:00), and no next-day-delivery-collection logic.
 * - The deadline hour is compared using the browser's local time, not a
 *   timezone-forced Europe/Berlin time — this deliberately MATCHES the checkout
 *   extension's own `getOrderDeadline`/`isDeliveryDateOrderable`
 *   (deliveryRules.js), which likewise use plain `new Date()`/`setHours()` in
 *   whatever timezone the customer's browser reports. Matching that behavior
 *   exactly (even though it isn't truly Berlin-time-correct for a customer outside
 *   CET) is what avoids a mismatch here — the goal is agreeing with the extension,
 *   not independently being "more correct" than it.
 *
 * The checkout UI extension (extensions/delivery in the app repo) remains
 * authoritative and will correct any of this after it mounts. Worst case (this
 * guess is wrong) is identical to today's behavior — one corrective recompute.
 */
(function () {
  var WAREHOUSE_BUNDESLAND = 'SL';
  var VALID_SHIPPING_DAYS = [2, 3, 4, 5]; // Tue–Fri
  var VALID_DELIVERY_DAYS = [3, 4, 5, 6]; // Wed–Sat
  var HOLIDAY_API_TIMEOUT_MS = 350;
  var SEARCH_HORIZON_DAYS = 14;

  function fetchWithTimeout(resource, options, timeoutMs) {
    return new Promise(function (resolve, reject) {
      var timeoutId = window.setTimeout(function () {
        reject(new Error('Request timed out'));
      }, timeoutMs);

      fetch(resource, options).then(
        function (response) {
          window.clearTimeout(timeoutId);
          resolve(response);
        },
        function (error) {
          window.clearTimeout(timeoutId);
          reject(error);
        }
      );
    });
  }

  var HolidayService = {
    cache: {},

    /** @returns {Promise<Object<string,string>>} map of "YYYY-MM-DD" -> holiday name */
    getHolidays: function (year) {
      var cacheKey = 'holidays_' + year + '_' + WAREHOUSE_BUNDESLAND;

      if (this.cache[cacheKey]) {
        return Promise.resolve(this.cache[cacheKey]);
      }

      try {
        var cached = window.localStorage.getItem(cacheKey);
        if (cached) {
          var parsed = JSON.parse(cached);
          this.cache[cacheKey] = parsed;
          return Promise.resolve(parsed);
        }
      } catch (e) {
        // localStorage is optional for this best-effort flow.
      }

      var self = this;
      return fetchWithTimeout(
        'https://feiertage-api.de/api/?jahr=' + year + '&nur_land=' + WAREHOUSE_BUNDESLAND,
        {},
        HOLIDAY_API_TIMEOUT_MS
      )
        .then(function (response) {
          if (!response.ok) {
            throw new Error('Holiday API failed with status ' + response.status);
          }
          return response.json();
        })
        .then(function (data) {
          var holidayDates = {};
          Object.keys(data).forEach(function (name) {
            var info = data[name];
            if (info && info.datum) {
              holidayDates[info.datum] = name;
            }
          });

          self.cache[cacheKey] = holidayDates;
          try {
            window.localStorage.setItem(cacheKey, JSON.stringify(holidayDates));
          } catch (e) {
            // localStorage is optional for this best-effort flow.
          }
          return holidayDates;
        })
        .catch(function () {
          // Fail open: treat as "no known holidays" rather than blocking checkout.
          return {};
        });
    },
  };

  function formatDate(date) {
    var year = date.getFullYear();
    var month = String(date.getMonth() + 1).padStart(2, '0');
    var day = String(date.getDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
  }

  function addDays(date, days) {
    var next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  var MONTH_NAMES = [
    'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
    'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember',
  ];

  /**
   * "23. Februar 2026, 08:00 Uhr" — mirrors deliveryRules.js's formatDeadlineGerman
   * exactly (including using the Date object's local getters, to match).
   */
  function formatDeadlineGerman(deadline) {
    var hour = String(deadline.getHours()).padStart(2, '0');
    var min = String(deadline.getMinutes()).padStart(2, '0');
    return deadline.getDate() + '. ' + MONTH_NAMES[deadline.getMonth()] + ' ' + deadline.getFullYear() + ', ' + hour + ':' + min + ' Uhr';
  }

  function isHoliday(date, holidaysByYear) {
    var holidayMap = holidaysByYear[date.getFullYear()];
    return Boolean(holidayMap && holidayMap[formatDate(date)]);
  }

  function isValidShippingDay(date, holidaysByYear) {
    return VALID_SHIPPING_DAYS.indexOf(date.getDay()) !== -1 && !isHoliday(date, holidaysByYear);
  }

  function isValidDeliveryDay(date, holidaysByYear) {
    if (VALID_DELIVERY_DAYS.indexOf(date.getDay()) === -1 || isHoliday(date, holidaysByYear)) {
      return false;
    }
    return isValidShippingDay(addDays(date, -1), holidaysByYear);
  }

  /**
   * Subtracts `count` business days (skipping Sat/Sun and Saarland holidays) from
   * `fromDate` — mirrors deliveryRules.js's subtractBusinessDays.
   */
  function subtractBusinessDays(fromDate, count, holidaysByYear) {
    var current = new Date(fromDate);
    var remaining = count;
    while (remaining > 0) {
      current = addDays(current, -1);
      var dow = current.getDay();
      if (dow === 0 || dow === 6) continue; // weekend
      if (isHoliday(current, holidaysByYear)) continue;
      remaining -= 1;
    }
    return current;
  }

  /**
   * The *normal* order deadline for a candidate delivery date: 1 business day
   * before its shipping date, 08:00 (browser-local, matching the extension — see
   * file header). Does not account for the fish/poultry-specific 2-business-day/
   * 03:00 deadline — that precision is out of scope here.
   * @returns {Date}
   */
  function getOrderDeadline(date, holidaysByYear) {
    var shippingDate = addDays(date, -1);
    var deadlineDate = subtractBusinessDays(shippingDate, 1, holidaysByYear);
    deadlineDate.setHours(8, 0, 0, 0);
    return deadlineDate;
  }

  function isDeliveryDateOrderable(date, now, holidaysByYear) {
    return now < getOrderDeadline(date, holidaysByYear);
  }

  function loadHolidayYears(years) {
    var uniqueYears = years.filter(function (year, index) {
      return years.indexOf(year) === index;
    });

    return Promise.all(
      uniqueYears.map(function (year) {
        return HolidayService.getHolidays(year).then(function (holidays) {
          return [year, holidays];
        });
      })
    ).then(function (entries) {
      var holidaysByYear = {};
      entries.forEach(function (entry) {
        holidaysByYear[entry[0]] = entry[1];
      });
      return holidaysByYear;
    });
  }

  /**
   * @returns {{deliveryDate: Date, deadline: Date}}
   */
  function findFirstValidDeliveryDate(holidaysByYear) {
    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var now = new Date();
    var firstStructurallyValid = null;

    for (var offset = 1; offset <= SEARCH_HORIZON_DAYS; offset += 1) {
      var candidate = addDays(today, offset);
      if (!isValidDeliveryDay(candidate, holidaysByYear)) continue;

      if (!firstStructurallyValid) firstStructurallyValid = candidate;

      if (isDeliveryDateOrderable(candidate, now, holidaysByYear)) {
        return { deliveryDate: candidate, deadline: getOrderDeadline(candidate, holidaysByYear) };
      }
      // Structurally valid but its deadline has already passed — keep searching.
    }
    // Fallback: every candidate's deadline had passed (should not happen within 14
    // days in practice) — prefer the first structurally-valid day over guessing
    // tomorrow blindly; the checkout extension corrects this either way.
    var fallback = firstStructurallyValid || addDays(today, 1);
    return { deliveryDate: fallback, deadline: getOrderDeadline(fallback, holidaysByYear) };
  }

  function getCartDrawerContext() {
    var marker = document.querySelector('[data-cart-drawer-delivery-seed]');
    var cartDrawer = document.querySelector('cart-drawer');

    return {
      hasFishOrPoultry: marker ? marker.dataset.hasFishPoultry === 'true' : false,
      isEmpty: cartDrawer ? cartDrawer.classList.contains('is-empty') : true,
    };
  }

  /**
   * Computes a best-effort delivery date/attributes and writes them to the cart.
   * @returns {Promise<boolean>} whether the write was attempted and succeeded.
   */
  function seedDeliveryAttributes() {
    var context = getCartDrawerContext();
    if (context.isEmpty) {
      return Promise.resolve(false);
    }

    var today = new Date();
    today.setHours(0, 0, 0, 0);
    var lastCandidate = addDays(today, SEARCH_HORIZON_DAYS);

    return loadHolidayYears([today.getFullYear(), lastCandidate.getFullYear()])
      .then(function (holidaysByYear) {
        var found = findFirstValidDeliveryDate(holidaysByYear);
        var deliveryDate = found.deliveryDate;
        var shippingDate = addDays(deliveryDate, -1);

        var attributes = {
          Liefertermin: formatDate(deliveryDate),
          Versandtermin: formatDate(shippingDate),
          Bestellfrist: formatDeadlineGerman(found.deadline),
          Fisch_oder_Geflügel: context.hasFishOrPoultry ? 'Ja' : 'Nein',
          Bundesland: WAREHOUSE_BUNDESLAND,
        };

        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({ attributes: attributes }),
        });
      })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Cart update failed with status ' + response.status);
        }
        return true;
      })
      .catch(function (error) {
        console.warn('Delivery attribute seeding skipped:', error);
        return false;
      });
  }

  window.seedDeliveryAttributes = seedDeliveryAttributes;
})();
